// ─── utils/style.js ───────────────────────────────────────────────────────────
// Color parsing, gradient extraction, shadow/border/background helpers.

export function extractRGBAFromColor(c) {
    if (!c || c === 'transparent' || c === 'none')
        return { type: 'solid', r: 255, g: 255, b: 255, a: 1 };

    const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*(?:\.\d+)?))?\)/);
    if (m) return { type: 'solid', r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };

    // Resolve named/hex colors via a temp element
    const tmp = document.createElement('div');
    tmp.style.color = c;
    document.body.appendChild(tmp);
    const comp = window.getComputedStyle(tmp).color;
    document.body.removeChild(tmp);

    const m2 = comp.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*(?:\.\d+)?))?\)/);
    if (m2) return { type: 'solid', r: +m2[1], g: +m2[2], b: +m2[3], a: m2[4] !== undefined ? +m2[4] : 1 };

    return { type: 'solid', r: 33, g: 33, b: 33, a: 1 };
}

export function extractGradientFromBackground(bgImage, doc, el) {
    const isLinear     = bgImage.includes('linear-gradient');
    const gradientType = isLinear ? 'Linear' : 'Radial';
    const inside       = bgImage.substring(bgImage.indexOf('(') + 1, bgImage.lastIndexOf(')'));

    let degree = 0;
    const dm   = inside.match(/(-?\d+\.?\d*)deg/);
    if (dm) degree = parseFloat(dm[1]);
    
    if (degree === 0) {
        const primaryClass = el?.classList?.[0];
        if (primaryClass) {
            degree = extractGradientAngleFromStyles(doc, primaryClass);
        }
    }

    const colorRegex     = /(rgba?\([^)]+\))\s*(\d+%?)?/g;
    const matches        = [...inside.matchAll(colorRegex)];
    const total          = matches.length;
    const gradientColors = matches.map((match, i) => {
        const rgba     = extractRGBAFromColor(match[1]);
        const position = match[2]
            ? parseFloat(match[2])
            : total === 1 ? 0 : Math.round((i / (total - 1)) * 100);
        return { r: rgba.r, g: rgba.g, b: rgba.b, a: rgba.a, position };
    });

    const base = gradientColors[0] || { r: 0, g: 0, b: 0, a: 1 };
    return {
        type:  'color',
        color: { r: base.r, g: base.g, b: base.b, a: base.a, type: 'gradient', gradientType, degree, gradientColors },
    };
}

export function extractGradientAngleFromStyles(doc, className) {
    let degree = 180; 

    const styles = Array.from(doc.querySelectorAll('style'));

    styles.forEach((styleEl) => {
        const css = styleEl.textContent;

        // Match the specific class block
        const classRegex = new RegExp(`\\.${className}\\s*\\{[^}]*\\}`, 'g');
        const blocks = css.match(classRegex) || [];

        blocks.forEach((block) => {
            const gradientMatch = block.match(/linear-gradient\(([^)]+)\)/);

            if (gradientMatch) {
                const inside = gradientMatch[1];

                const degMatch = inside.match(/(-?\d+\.?\d*)deg/);
                if (degMatch) {
                    degree = parseFloat(degMatch[1]);
                }
            }
        });
    });

    return degree;
}

export function extractShadowProperties(boxShadow) {
    if (!boxShadow || boxShadow === 'none')
        return { color: { type: 'solid', r: 255, g: 255, b: 255, a: 1 }, axes: { x: '0', y: '0', b: '0' }, show: false };

    const p = boxShadow.split(' ').filter(Boolean);
    return {
        color: extractRGBAFromColor(p.slice(3).join(' ') || 'rgba(0,0,0,0.5)'),
        axes:  {
            x: (parseFloat(p[0]) || 0).toString(),
            y: (parseFloat(p[1]) || 0).toString(),
            b: (parseFloat(p[2]) || 0).toString(),
        },
        show: true,
    };
}

export function extractBorderRadius(style) {
    const tl = parseFloat(style.borderTopLeftRadius     || '0').toString();
    const tr = parseFloat(style.borderTopRightRadius    || '0').toString();
    const bl = parseFloat(style.borderBottomLeftRadius  || '0').toString();
    const br = parseFloat(style.borderBottomRightRadius || '0').toString();
    return { lock: tl === tr && tr === br && br === bl, topLeft: tl, topRight: tr, bottomLeft: bl, bottomRight: br };
}

/**
 * Extracts a background descriptor from a computed style object.
 * Returns null when no meaningful background is found.
 */
export function extractBackground(style) {
    const bgi = style.backgroundImage;
    const bgc = style.backgroundColor;
    const hi  = bgi && bgi !== 'none';
    const hc  = bgc && bgc !== 'rgba(0, 0, 0, 0)' && bgc !== 'transparent';

    if (hi && (bgi.includes('linear-gradient') || bgi.includes('radial-gradient')))
        return extractGradientFromBackground(bgi);

    if (hi && bgi.includes('url(')) {
        const m = bgi.match(/url\(['"]?(.*?)['"]?\)/);
        if (m?.[1]) return {
            type:     'image',
            opacity:  100,
            media:    m[1],
            scale:    style.backgroundSize.includes('cover')   ? 'scale-to-fill'
                    : style.backgroundSize.includes('contain') ? 'scale-to-fit' : 'original',
            position: style.backgroundPosition || 'center',
            blur:     0,
        };
    }

    if (hc) return { type: 'solid', ...extractRGBAFromColor(bgc) };
    return null;
}

/**
 * Extracts a section's background, falling back to the body background when
 * the section itself has none.
 */
export function extractSectionBackground(style, el, doc) {
    const bgi  = style.backgroundImage;
    const bgc  = style.backgroundColor;
    const hImg = bgi && bgi !== 'none';
    const hClr = bgc && bgc !== 'rgba(0, 0, 0, 0)' && bgc !== 'transparent';

    if (hImg && bgi.includes('linear-gradient')) return extractGradientFromBackground(bgi, doc, el);

    if (hImg && bgi.includes('url(')) {
        const m = bgi.match(/url\(['"]?(.*?)['"]?\)/);
        if (m?.[1]) {
            const hasVideo = !!el.querySelector('video, iframe[src*="youtube"], iframe[src*="vimeo"]');
            return {
                opacity:  100,
                color:    hClr ? extractRGBAFromColor(bgc) : null,
                scale:    style.backgroundSize.includes('cover')   ? 'scale-to-fill'
                        : style.backgroundSize.includes('contain') ? 'scale-to-fit' : 'original',
                position: style.backgroundPosition || 'center',
                blur:     0,
                type:     hasVideo ? 'video' : 'image',
                media:    m[1],
            };
        }
    }

    if (hClr) return { opacity: 100, type: 'color', color: extractRGBAFromColor(bgc) };

    // ── Fallback: body background ─────────────────────────────────────────────
    if (!doc) return null;
    const bs = window.getComputedStyle(doc.body);
    const bi = bs.backgroundImage;
    const bc = bs.backgroundColor;
    const hbi = bi && bi !== 'none';
    const hbc = bc && bc !== 'rgba(0, 0, 0, 0)' && bc !== 'transparent';

    if (hbi && bi.includes('linear-gradient')) return extractGradientFromBackground(bi);

    if (hbi && bi.includes('url(')) {
        const m = bi.match(/url\(['"]?(.*?)['"]?\)/);
        if (m?.[1]) return {
            opacity:  100,
            color:    hbc ? extractRGBAFromColor(bc) : null,
            scale:    bs.backgroundSize.includes('cover')   ? 'scale-to-fill'
                    : bs.backgroundSize.includes('contain') ? 'scale-to-fit' : 'original',
            position: bs.backgroundPosition || 'center',
            blur:     0,
            type:     'image',
            media:    m[1],
        };
    }

    if (hbc) return { opacity: 100, type: 'color', color: extractRGBAFromColor(bc) };
    return null;
}

// Shared zero-shadow constant
export const NO_SHADOW = {
    color: { type: 'solid', r: 255, g: 255, b: 255, a: 1 },
    axes:  { x: '0', y: '0', b: '0' },
    show:  false,
};